import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import type { EmployeeAutoFill } from './EmployeeSearch'
import { InfoTooltip, inputStyles, labelStyles, selectStyles } from '../JobAutoCalculatorHelpers'

interface Props {
  onSubmit: (fill: EmployeeAutoFill) => void
}

type FuelChoice = '' | 'elektro' | 'hybrid' | 'verbrenner'

// Mirrors EmployeeSearch.inferGwvPercentage: the GwV rate is 0.25 for BEV,
// 0.5 for hybrids, 1.0 otherwise. gwvPercentage is not consumed by the
// eligibility flow today (handleAutoFill ignores it), but we set it correctly
// so the emitted payload stays a faithful EmployeeAutoFill.
function inferGwvPercentage(fuel: FuelChoice): 0.25 | 0.5 | 1.0 {
  if (fuel === 'elektro') return 0.25
  if (fuel === 'hybrid') return 0.5
  return 1.0
}

/**
 * Manual ("blank") entry path for the Gehaltscheck. Lets People-Ops run the
 * eligibility check when there is no open/eligible JobAuto request to look up
 * (e.g. an already-active subscription, a B2C→JobAuto switch, or an A→B
 * employee swap). It never calls the employee-lookup endpoint — the operator
 * types the JobAuto rate directly and proceeds to the same Step 2 as the
 * search path, emitting the identical EmployeeAutoFill payload.
 */
export default function ManualSubForm({ onSubmit }: Props) {
  const [rateBrutto, setRateBrutto] = useState(0)
  const [carLabel, setCarLabel] = useState('')
  const [fuel, setFuel] = useState<FuelChoice>('')
  const [isSwap, setIsSwap] = useState(false)
  const [previousSubscriptionId, setPreviousSubscriptionId] = useState('')

  const canSubmit = rateBrutto > 0

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    const trimmedLabel = carLabel.trim()
    const trimmedPrev = previousSubscriptionId.trim()
    onSubmit({
      gehaltsumwandlung: rateBrutto,
      bruttolistenpreis: null,
      gwvPercentage: inferGwvPercentage(fuel),
      carLabel: trimmedLabel || 'JobAuto (manuell)',
      fuel: fuel || null,
      pendingApproval: false,
      isSwap,
      previousSubscriptionId: isSwap && trimmedPrev ? trimmedPrev : null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Brutto conversion rate — the only calc-relevant input; Step 2 nets it. */}
      <div className="space-y-1.5">
        <label className={labelStyles}>
          Brutto-Umwandlungsrate / Monat
          <InfoTooltip>
            Die vom JobAuto-Portal angezeigte Brutto-Rate (inkl. MwSt.) des Fahrzeugs. Der
            Gehaltscheck rechnet sie automatisch auf die Nettorate um (÷ 1,19), genau wie im
            Suche-Modus.
          </InfoTooltip>
        </label>
        <div className="relative">
          <input
            type="number"
            min={0}
            className={inputStyles}
            value={rateBrutto || ''}
            placeholder="z.B. 899"
            onChange={e => setRateBrutto(Math.max(0, Number(e.target.value)))}
            autoFocus
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">€</span>
        </div>
      </div>

      {/* Optional display-only fields */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className={labelStyles}>
            Fahrzeug
            <span className="text-xs font-normal text-zinc-400">optional</span>
          </label>
          <input
            type="text"
            className={inputStyles}
            value={carLabel}
            placeholder="z.B. Cupra Born"
            onChange={e => setCarLabel(e.target.value)}
          />
        </div>

        <div className="space-y-1.5">
          <label className={labelStyles}>
            Antrieb
            <span className="text-xs font-normal text-zinc-400">optional</span>
          </label>
          <select
            className={selectStyles}
            value={fuel}
            onChange={e => setFuel(e.target.value as FuelChoice)}
          >
            <option value="">—</option>
            <option value="elektro">Elektro</option>
            <option value="hybrid">Hybrid</option>
            <option value="verbrenner">Verbrenner</option>
          </select>
        </div>
      </div>

      {/* Swap handling — display hint + enables the exclude-returned-car UX in Step 2. */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500/20"
            checked={isSwap}
            onChange={e => setIsSwap(e.target.checked)}
          />
          Es handelt sich um einen Swap (bestehendes JobAuto wird ersetzt)
          <InfoTooltip>
            Bei einem Swap gibt der Mitarbeiter ein bestehendes JobAuto zurück. Markiere das
            zurückgegebene Fahrzeug in Schritt 2, damit es nicht doppelt gezählt wird.
          </InfoTooltip>
        </label>
        {isSwap && (
          <div className="space-y-1.5">
            <label className={labelStyles}>
              Vorherige Subscription-ID
              <span className="text-xs font-normal text-zinc-400">optional</span>
            </label>
            <input
              type="text"
              className={inputStyles}
              value={previousSubscriptionId}
              placeholder="z.B. 52345797126"
              onChange={e => setPreviousSubscriptionId(e.target.value)}
            />
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        className="w-full flex items-center justify-center gap-1.5 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Weiter zum Gehaltscheck <ArrowRight className="h-4 w-4" />
      </button>
    </form>
  )
}
